// Copyright (c) 2019 Celadon Development LLC, All rights reserved.
// Author Yana Sidorova <yana.sidorova@celadon.ae>
import { put, call, takeEvery } from 'redux-saga/effects';

import history from '../../../../../../../services/history';
import { BANK_TRANSFER, CHARITY } from '../../../../../../../config/constants';
import {
  profileService,
  changePasswordService,
  profileEditService,
  validatePayPalEmailService,
  verifyPhoneNumberService,
  verifyBankAccountService
} from './service';
import * as profileActions from './actions';
import * as registerActions from '../../../../../../register/store/actions';
import * as basketActions from '../../../../../../basket/store/actions';
import * as chooseDeliveryActions from '../../../../../../choose-delivery/store/actions';
import * as types from './actionTypes';

function* profileSaga(payload) {
  const res = yield call(profileService, payload);
  if (res.ok) {
    const profile = res.body;
    yield put(profileActions.profileRequestSuccess(profile));
  } else {
    yield put(profileActions.profileRequestFailed());
  }
}

function* profileEditSaga(payload) {
  const res = yield call(profileEditService, payload);
  if (res.ok) {
    if (payload.pageInfo.page === 'checkout' && payload.to) {
      history.push(payload.to);
    } else if (payload.pageInfo.page === 'getpaid' && payload.to) {
      const CustomerId = localStorage.getItem('loggedID');
      const CartID = localStorage.getItem('CartID');
      const Token = localStorage.getItem('token');
      const paymentType = JSON.parse(localStorage.getItem('paymentType'));
      const CharityID = JSON.parse(localStorage.getItem('CharityID'));
      if (CustomerId && CartID) {
        if (paymentType === CHARITY) {
          yield put(chooseDeliveryActions.confirmOrderForCharity({
            CustomerId,
            CartID,
            Token,
            paymentType: BANK_TRANSFER,
            CharityID,
          }));
        } else {
          yield put(chooseDeliveryActions.requestConfirm({
            CustomerId,
            CartID,
            Token,
            paymentType,
          }));
        }
        const carId = localStorage.getItem('CartID');
        yield put(basketActions.getIsEligibleForHomePickup(carId, payload.to));
      }
    } else {
      yield put(profileActions
        .profileEditRequestSuccess({
          status: true,
          [payload.pageInfo.page]: 'Successfully edited!',
        }));
    }
  } else {
    yield put(profileActions
      .profileEditRequestFailed({ status: false, [payload.pageInfo.page]: res.body.Reason }));
  }
}

function* changePasswordSaga(payload) {
  const token = localStorage.getItem('token');
  const CustomerId = localStorage.getItem('loggedID');
  const res = yield call(changePasswordService, { ...payload, token, CustomerId });
  if (res.ok) {
    yield put(profileActions
      .setSuccess({ status: true, profile: 'Password successfully changed.' }));
  } else {
    yield put(profileActions.changeErrors('password', res.body.Reason));
  }
}

function* validatePayPalEmailSaga(payload) {
  const id = localStorage.getItem('loggedID');
  const token = localStorage.getItem('token');
  const initialPaypal = JSON.parse(localStorage.getItem('initialPaypal'));
  const profile = JSON.parse(localStorage.getItem('profile'));
  const aboutPage = JSON.parse(localStorage.getItem('aboutPage'));
  const isPayPalEdit = (initialPaypal === payload.paypal.PaypalEmail
    && aboutPage.page === 'getpaid');
  const isPayPalEmpty = (payload.paypal.PaypalEmail === null
    && payload.paypal.ConfirmPaypalEmail === null);

  if (isPayPalEdit || isPayPalEmpty) {
    yield put(profileActions.requestProfileEdit(
      profile,
      id,
      token,
      aboutPage,
      '/choose-delivery',
    ));
  } else {
    const res = yield call(validatePayPalEmailService, payload.paypal);
    if (res.ok) {
      if (res.body.Success) {
        yield put(profileActions.requestProfileEdit(
          profile,
          id,
          token,
          aboutPage,
          '/choose-delivery',
        ));
        yield put(profileActions.setIsChangeFirstTime(true));
      } else {
        yield put(profileActions.validatePayPalEmailSuccess(res.body));
      }
    }
  }
}

function* verifyPhoneNumberSaga(payload) {
  const { dataForNextRequest } = payload;
  let errorText;
  if (payload.data.phone_number) errorText = 'Invalid mobile number.';
  else errorText = 'Please enter your mobile number.';

  const res = yield call(verifyPhoneNumberService, payload.data);

  if (res.ok) {
    if (res.body.result && res.body.carrier.type === 'mobile') {
      if (dataForNextRequest.data) {
        const { data, toCheckout } = dataForNextRequest;
        yield put(registerActions.requestRegister(data, toCheckout));
      } else {
        const {
          profile, id, token, pageInfo,
        } = dataForNextRequest;
        yield put(profileActions.requestProfileEdit(profile, id, token, pageInfo));
      }
    } else if (dataForNextRequest.data) {
      yield put(registerActions.phoneNumberValidationFailed({ common: errorText }));
    } else {
      yield put(profileActions.verifyPhoneNumberFailed({
        status: false,
        profile: errorText,
      }));
    }
  }
}

function* verifyBankAccountSaga(payload) {
  const { dataForNextRequest } = payload;
  const { sortCode, accountNumber } = payload.data;
  const commonErrorText = 'Invalid Sort Code or Account Number.';
  const errorTextSortCode = 'Sort Code must be numeric and 6 in length.';
  const errorTextAccountNumber = 'Account Number must be numeric and 8 in length.';

  const res = yield call(verifyBankAccountService, payload.data);

  if (res.ok && accountNumber.length === 8) {
    if (res.body.bankValidate.isCorrect) {
      const {
        profile, id, token, pageInfo, to
      } = dataForNextRequest;
      yield put(profileActions.requestProfileEdit(profile, id, token, pageInfo, to));
    } else {
      yield put(profileActions.verifyBankAccountFailed({
        status: false,
        payment: commonErrorText,
        getpaid: commonErrorText,
      }));
    }
  } else {
    const regex = /^[0-9]+$/;
    const errorText = (sortCode.length !== 6 || !regex.test(sortCode)) ? errorTextSortCode : (
      (accountNumber.length !== 8 || !regex.test(sortCode))
        ? errorTextAccountNumber : commonErrorText
    );
    yield put(profileActions.verifyBankAccountFailed({
      status: false,
      payment: errorText,
      getpaid: errorText,
    }));
  }
}

function* checkoutForTradeCustomerSaga() {
  const CustomerId = localStorage.getItem('loggedID');
  const CartID = localStorage.getItem('CartID');
  const Token = localStorage.getItem('token');
  const paymentType = JSON.parse(localStorage.getItem('PaymentTypeForTradeCustomer'));

  if (CustomerId && CartID) {
    yield put(chooseDeliveryActions.requestConfirm({
      CustomerId,
      CartID,
      Token,
      paymentType,
    }));
    yield put(basketActions.getIsEligibleForHomePickup(CartID, '/choose-delivery'));
  }
}

const profileSagas = [
  takeEvery(types.PROFILE_REQUEST, profileSaga),
  takeEvery(types.PROFILE_CHANGE_PASSWORD_REQUEST, changePasswordSaga),
  takeEvery(types.PROFILE_EDIT_REQUEST, profileEditSaga),
  takeEvery(types.VALIDATE_PAYPAL_EMAIL_REQUEST, validatePayPalEmailSaga),
  takeEvery(types.VERIFY_PHONE_NUMBER, verifyPhoneNumberSaga),
  takeEvery(types.VERIFY_BANK_ACCOUNT, verifyBankAccountSaga),
  takeEvery(types.CHECKOUT_FOR_TRADE_CUSTOMER, checkoutForTradeCustomerSaga),
];

export default profileSagas;
